import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Share2, Leaf, Euro } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Link } from "react-router-dom";
const CarbonMarketArticle = () => {
  const {
    t
  } = useLanguage();
  const emissionsData = [{
    sector: t('article.carbon.chart.buildings'),
    current: 875,
    target2030: 507
  }, {
    sector: t('article.carbon.chart.transport'),
    current: 620,
    target2030: 514
  }, {
    sector: t('article.carbon.chart.industry'),
    current: 245,
    target2030: 142
  }];
  const shareArticle = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
  };
  return <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('common.back')}
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-blue-900">{t('header.title')}</h1>
                <p className="text-blue-600">{t('header.subtitle')}</p>
              </div>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <article className="max-w-4xl mx-auto">
          {/* Article Header */}
          <div className="mb-8">
            <Badge className="mb-4 bg-blue-500">
              {t('common.newPolicy')}
            </Badge>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.carbon.title')}
            </h1>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center text-gray-600">
                <Calendar className="h-4 w-4 mr-2" />
                <span>{t('article.carbon.date')}</span>
                <span className="mx-2">•</span>
                <span>{t('article.carbon.readTime')}</span>
              </div>
              <Button onClick={shareArticle} variant="outline" size="sm">
                <Share2 className="h-4 w-4 mr-2" />
                {t('common.share')}
              </Button>
            </div>
          </div>

          {/* Featured Image */}
          <div className="mb-8">
            <img src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="European carbon market emissions trading" className="w-full rounded-lg shadow-lg sk-edit-loading h-[384px] object-cover" />
            <p className="text-sm text-gray-500 mt-2 text-center">
              {t('article.carbon.imageCaption')}
            </p>
          </div>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 leading-relaxed">
              {t('article.carbon.lead')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.carbon.section1.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.carbon.section1.content')}
            </p>

            {/* Emissions Chart */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Leaf className="h-5 w-5 mr-2 text-green-600" />
                  {t('article.carbon.chart.title')}
                </CardTitle>
                <CardDescription>{t('article.carbon.chart.description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={emissionsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="sector" />
                    <YAxis />
                    <Tooltip formatter={value => [`${value} Mt CO2`, '']} />
                    <Bar dataKey="current" fill="#ef4444" name={t('article.carbon.chart.current')} />
                    <Bar dataKey="target2030" fill="#10b981" name={t('article.carbon.chart.target2030')} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.carbon.section2.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.carbon.section2.content')}
            </p>

            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-green-900 mb-3">
                {t('article.carbon.keyFeatures.title')}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-2xl font-bold text-green-800">2027</div>
                  <div className="text-sm text-green-600">{t('article.carbon.keyFeatures.startDate')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-800">42%</div>
                  <div className="text-sm text-green-600">{t('article.carbon.keyFeatures.reductionTarget')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-800">€45</div>
                  <div className="text-sm text-green-600">{t('article.carbon.keyFeatures.priceFloor')}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-800">€104B</div>
                  <div className="text-sm text-green-600">{t('article.carbon.keyFeatures.marketValue')}</div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.carbon.section3.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.carbon.section3.content')}
            </p>

            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">
                {t('article.carbon.timeline.title')}
              </h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-blue-600 rounded-full mr-4"></div>
                  <div>
                    <div className="font-semibold">2024-2026</div>
                    <div className="text-sm text-gray-600">{t('article.carbon.timeline.preparation')}</div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-600 rounded-full mr-4"></div>
                  <div>
                    <div className="font-semibold">2027</div>
                    <div className="text-sm text-gray-600">{t('article.carbon.timeline.launch')}</div>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-yellow-600 rounded-full mr-4"></div>
                  <div>
                    <div className="font-semibold">2030</div>
                    <div className="text-sm text-gray-600">{t('article.carbon.timeline.target')}</div>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.carbon.section4.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.carbon.section4.content')}
            </p>

            {/* Source Link */}
            <div className="bg-gray-50 p-6 rounded-lg mb-8 border-l-4 border-l-blue-500">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {t('article.source.title')}
              </h3>
              <p className="text-gray-600 mb-3">{t('article.source.description')}</p>
              <a href="https://climate.ec.europa.eu/news-other-reads/news/ensuring-stable-start-europes-new-carbon-market-buildings-and-road-transport-2025-10-21_en" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <span className="mr-2">🏛️</span>
                {t('article.source.readOriginal')}
              </a>
              <p className="text-sm text-gray-500 mt-2">
                {t('article.source.publisher')}: European Commission
              </p>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-12 pt-8 border-t">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              {t('article.relatedArticles')}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/article/freight-rates">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('industry.freight.title')}</CardTitle>
                    <CardDescription>{t('industry.freight.date')}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/article/train-connection">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <CardTitle className="text-lg">{t('industry.train.title')}</CardTitle>
                    <CardDescription>{t('industry.freight.date')}</CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>;
};
export default CarbonMarketArticle;